/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { Button, Empty, Tooltip, message } from 'antd';
import {
  CheckCircleOutlined,
  CrownOutlined,
  DownloadOutlined,
  ProfileOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { OffersResultsRow } from './OffersResultsRow';
import {
  buildMergedEvaluationVouchers,
  EvaluationValue,
  EvaluationVoucherLike,
  EvaluationVoucherResponseLike,
  getEvaluationOption,
  getOfferVendorName,
  getResultValue,
  isResultOffer,
  ResultContractLike,
  ResultOfferLike,
} from './resultsData';
import {
  computeOfferSerials,
  getOfferSerial,
} from '../OffersEvaluation/evaluationData';
import {
  BulkOfferEntry,
  downloadAllOffersZip,
} from '~src/utils/downloader/downloadOfferZip';
import { useStore, DownloadingState } from '~src/store';

type OffersResultsViewProps = {
  projectId: number;
  tenderId: number;
  contract: ResultContractLike;
  offers: ResultOfferLike[];
  vouchers: EvaluationVoucherLike[];
  voucherResponsesByOfferId?: Record<number, EvaluationVoucherResponseLike[]>;
  doSetWinner?: (params: any) => any;
  doRemoveWinner?: (params: any) => any;
};

export const OffersResultsView: FC<OffersResultsViewProps> = ({
  projectId,
  tenderId,
  contract,
  offers,
  vouchers,
  voucherResponsesByOfferId = {},
  doSetWinner,
  doRemoveWinner,
}) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();

  const [resultValueByOfferId, setResultValueByOfferId] = useState<
    Record<number, EvaluationValue>
  >({});

  const [isBulkDownloading, setIsBulkDownloading] = useState(false);

  const contractOffers = useMemo(
    () => offers.filter((offer) => offer.contract_id === contract.id),
    [offers, contract.id]
  );

  const serialMap = useMemo(() => computeOfferSerials(contractOffers), [
    contractOffers,
  ]);

  const handleBulkDownload = async () => {
    setIsBulkDownloading(true);

    // Activate the download progress modal
    useStore.getState().setIsOfferDownload(true);
    useStore.getState().changeDownloadingActive(true);
    useStore.getState().changeDownloadingState(DownloadingState.PREPARING);

    try {
      const offersData: BulkOfferEntry[] = contractOffers.map((offer) => ({
        offerId: offer.id,
        offerName: `${getOfferSerial(offer.id, serialMap)} ${getOfferVendorName(
          offer,
          intl
        )}`,
        offerSerial: getOfferSerial(offer.id, serialMap),
        mergedVouchers: buildMergedEvaluationVouchers(
          vouchers,
          voucherResponsesByOfferId[offer.id] || [],
          intl
        ),
      }));
      await downloadAllOffersZip({
        projectId,
        tenderId,
        contractId: contract.id,
        contractName: contract.name || `Contract_${contract.id}`,
        offersData,
        tabLabel: 'Results',
      });
    } catch {
      message.error(intl.formatMessage({ id: 'attachment-download-failed' }));
    } finally {
      setIsBulkDownloading(false);
      // Modal will auto-close after FINISHING state (handled by saveFilesToZip)
    }
  };

  useEffect(() => {
    const nextState = contractOffers.reduce<Record<number, EvaluationValue>>(
      (acc, offer) => {
        acc[offer.id] = getResultValue(offer);
        return acc;
      },
      {}
    );

    setResultValueByOfferId(nextState);
  }, [contractOffers]);

  const filteredOffers = useMemo(() => contractOffers.filter(isResultOffer), [
    contractOffers,
  ]);

  const summary = useMemo(() => {
    const total = filteredOffers.length;
    const priority = filteredOffers.filter(
      (o) => (resultValueByOfferId[o.id] ?? getResultValue(o)) === 5
    ).length;
    const selected = total - priority;
    const hasWinner = !!contract.winner_offer?.share_key_id;

    return { total, selected, priority, hasWinner };
  }, [filteredOffers, resultValueByOfferId, contract.winner_offer]);

  const isWinner = (offer: ResultOfferLike) =>
    !!contract.winner_offer?.share_key_id &&
    contract.winner_offer.share_key_id === offer.share_key_id;

  const handleSetWinner = (offer: ResultOfferLike) => () => {
    doSetWinner?.({
      projectId,
      tenderId,
      contractId: contract.id,
      body: { offer_id: offer.id },
    });
  };

  const handleRemoveWinner = () => {
    doRemoveWinner?.({ projectId, tenderId, contractId: contract.id });
  };

  const getOfferResponses = (offerId: number) =>
    voucherResponsesByOfferId[offerId] || [];

  const isEmpty = filteredOffers.length === 0;

  return (
    <div css={styles.wrapper}>
      <div css={styles.header}>
        <div>
          <div css={styles.eyebrow}>
            {intl.formatMessage({
              id: 'offers-results-title',
            })}
          </div>

          <div css={styles.description}>
            {intl.formatMessage({
              id: 'offers-results-description',
            })}
          </div>
        </div>
      </div>

      <div css={styles.summaryGrid}>
        <div css={styles.summaryCard}>
          <div css={[styles.summaryIcon, styles.totalIcon]}>
            <ProfileOutlined />
          </div>
          <div>
            <div css={styles.summaryValue}>{summary.total}</div>
            <div css={styles.summaryLabel}>
              {intl.formatMessage({
                id: 'offers-results-summary-total',
              })}
            </div>
          </div>
        </div>

        <div css={styles.summaryCard}>
          <div css={[styles.summaryIcon, styles.selectedIcon]}>
            <CheckCircleOutlined />
          </div>
          <div>
            <div css={styles.summaryValue}>{summary.selected}</div>
            <div css={styles.summaryLabel}>
              {intl.formatMessage({
                id: 'offers-results-summary-selected',
              })}
            </div>
          </div>
        </div>

        <div css={styles.summaryCard}>
          <div css={[styles.summaryIcon, styles.priorityIcon]}>
            <CrownOutlined />
          </div>
          <div>
            <div css={styles.summaryValue}>{summary.priority}</div>
            <div css={styles.summaryLabel}>
              {intl.formatMessage({
                id: 'offers-results-summary-priority',
              })}
            </div>
          </div>
        </div>

        <div css={styles.summaryCard}>
          <div css={[styles.summaryIcon, styles.winnerIcon]}>
            <TrophyOutlined />
          </div>
          <div>
            <div css={styles.summaryValue}>{summary.hasWinner ? 1 : 0}</div>
            <div css={styles.summaryLabel}>
              {intl.formatMessage({
                id: 'offers-results-summary-winner',
              })}
            </div>
          </div>
        </div>
      </div>

      <div css={styles.toolbar}>
        <Tooltip
          title={intl.formatMessage({ id: 'offers-bulk-download-tooltip' })}>
          <Button
            size="small"
            css={styles.downloadAllButton}
            icon={<DownloadOutlined />}
            loading={isBulkDownloading}
            onClick={handleBulkDownload}>
            {intl.formatMessage({ id: 'offers-bulk-download-label' })}
          </Button>
        </Tooltip>
      </div>

      {isEmpty ? (
        <div css={styles.emptyBlock}>
          <Empty
            description={intl.formatMessage({
              id: 'offers-results-empty',
            })}
          />
        </div>
      ) : (
        <div css={styles.list}>
          {filteredOffers.map((offer) => {
            const value =
              resultValueByOfferId[offer.id] ?? getResultValue(offer);
            const option = getEvaluationOption(value);

            return (
              <div
                key={offer.id}
                css={[
                  styles.rowFrame,
                  {
                    borderLeftColor: option.color,
                  },
                ]}>
                <OffersResultsRow
                  projectId={projectId}
                  tenderId={tenderId}
                  contractId={contract.id}
                  contractName={contract.name || ''}
                  offer={offer}
                  serial={getOfferSerial(offer.id, serialMap)}
                  vouchers={vouchers}
                  responses={getOfferResponses(offer.id)}
                  resultValue={value}
                  isWinner={isWinner(offer)}
                  onSetWinner={handleSetWinner(offer)}
                  onRemoveWinner={handleRemoveWinner}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const styleFn: ThemedStyleFn = () => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    paddingTop: 20,
    width: '100%',
  },

  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 24,
  },

  eyebrow: {
    color: '#52c41a',
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 4,
  },

  description: {
    marginTop: 10,
    maxWidth: 780,
    color: '#666666',
    fontSize: 14,
    lineHeight: 1.6,
  },

  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(120px, 1fr))',
    gap: 12,
  },

  summaryCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 14,
    border: '1px solid #e8e8e8',
    borderRadius: 6,
    background: '#ffffff',
  },

  summaryIcon: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
  },

  totalIcon: {
    background: '#f6ffed',
    color: '#52c41a',
  },

  selectedIcon: {
    background: '#f6ffed',
    color: '#52c41a',
  },

  priorityIcon: {
    background: '#fcffe6',
    color: '#7cb305',
  },

  winnerIcon: {
    background: '#fffbe6',
    color: '#faad14',
  },

  summaryValue: {
    fontSize: 20,
    fontWeight: 700,
    lineHeight: 1,
    color: '#222222',
  },

  summaryLabel: {
    marginTop: 4,
    color: '#777777',
    fontSize: 12,
  },

  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 12,
    padding: '10px 12px',
    border: '1px solid #e8e8e8',
    borderRadius: 6,
    background: '#ffffff',
  },

  downloadAllButton: {
    height: 28,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '0 10px',
    whiteSpace: 'nowrap',
  },

  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },

  rowFrame: {
    borderLeft: '3px solid transparent',
    borderRadius: 6,
  },

  emptyBlock: {
    padding: '40px 0',
    border: '1px solid #e8e8e8',
    borderRadius: 6,
    background: '#ffffff',
  },
});
